import React, {useCallback, useContext, useEffect, useState} from 'react';
import {FormFieldDesignContext} from '@src/contexts/FormFieldDesignContext';
import {FormAPIContext} from '@src/contexts/FormAPIContext';
import ExcuseForm from '../ExcuseForm';
import Portal from '@src/components/Portal/Portal';
import usePermissionGranted from '@src/hooks/usePermissionGranted';

export default function OrderReactionTimeExpired(props: {
    orderId: string | number,
    controllerName: string,
    message?: string,
    shouldDisplay: boolean,
    children?: React.ReactNode,
}) {
    const [designContext, setDesignContextValue] = useContext(FormFieldDesignContext);
    const [wasDisabled] = useState(designContext.fieldsAreDisabled);
    const formApiContext = useContext(FormAPIContext);
    const [shouldIgnore, setShouldIgnore] = useState(false);

    const overlayDisplayed = formApiContext?.overlayDisplayed;
    const displayOverlay = formApiContext?.displayOverlay;
    const orderId = props.orderId;
    const shouldDisplay = !shouldIgnore && props.shouldDisplay;

    const ignorePermission = 'orders@can-ignore-issue-of-status-expiration';
    const canIgnore = usePermissionGranted(ignorePermission);

    useEffect(() => {
        if (!props.shouldDisplay || canIgnore) {
            return;
        }

        setDesignContextValue(was => ({...was, fieldsAreDisabled: true}));

        return () => {
            setDesignContextValue(was => ({...was, fieldsAreDisabled: wasDisabled}));
        };
    }, [canIgnore, props.shouldDisplay, setDesignContextValue, wasDisabled]);

    useEffect(() => {
        const rollback = () => {
            if (overlayDisplayed && displayOverlay) {
                displayOverlay(false);
            }
        };

        if (!shouldDisplay) {
            rollback();

            return;
        }

        if (!overlayDisplayed && displayOverlay) {
            displayOverlay(true);
        }

        return rollback;
    }, [designContext, overlayDisplayed, displayOverlay, shouldDisplay, wasDisabled]);

    const displayExcuseForm = useCallback(() => setShouldIgnore(false), []);

    return <>
        <Portal
                container={formApiContext?.loaderContentContainerRef?.current}
                display={props.shouldDisplay}
        >
            <div className="floating-button__wrapper">
                <button
                        type="button"
                        className="floating-button"
                        onClick={displayExcuseForm}
                >
                    <i className="fa fa-exclamation-triangle"/>
                </button>
            </div>
        </Portal>

        {(shouldDisplay && formApiContext?.overlayRef?.current) ? (
                <ExcuseForm
                        portal={formApiContext?.overlayRef}
                        controllerName={props.controllerName}
                        modelId={orderId}
                        modelName="Orders"
                        sendToRouteName="orders.specify-status-expiration-reason"
                        alertMessage={props.message}
                        onIgnore={setShouldIgnore}
                />
        ) : null}

        {props.children}
    </>;
}
